import {
  ServiceState,
  CreateService,
  UpdateService,
} from "../../types/service";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiResource, servicesAPI } from "../../services/api";
import { cacheServices, getCachedServices } from "../../utils/indexedDB";

const servicesApi = apiResource("services");

const initialState: ServiceState = {
  services: [],
  clientGroups: [],
  clientGroupsSearch: "",
  clientGroupsPagination: {
    page: 1,
    total: 0,
    pages: 0,
  },
  currentService: null,
  loading: false,
  isLoaded: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Get all services
export const getServices = createAsyncThunk(
  "services/getServices",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await servicesApi.getAll(params);
      const data = response.data.data;

      // Cache for offline use (best-effort)
      await cacheServices(data.services).catch((err) => {
        console.warn("[Offline] Failed to cache services:", err);
      });

      return data;
    } catch (error: any) {
      // If offline or network error, try to serve from cache
      if (!navigator.onLine || error.code === "ERR_NETWORK") {
        try {
          const cached = await getCachedServices();
          if (cached && cached.length > 0) {
            // console.log("[Offline] Serving services from cache:", cached.length);
            return {
              services: cached,
              pagination: {
                page: 1,
                limit: cached.length,
                total: cached.length,
                pages: 1,
              },
            };
          }
        } catch (cacheErr) {
          console.error("[Offline] Services cache retrieval failed:", cacheErr);
        }
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch services",
      );
    }
  },
);

// Get client-specific services grouped by client (Client Specific tab)
export const getClientServiceGroups = createAsyncThunk(
  "services/getClientServiceGroups",
  async (
    params: {
      search?: string;
      page?: number;
      limit?: number;
      partnerId?: string;
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await servicesAPI.getClientGroups(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch client services",
      );
    }
  },
);

// Get single service
export const getService = createAsyncThunk(
  "services/getService",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await servicesApi.getOne(id);
      return response.data.data.service;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch service",
      );
    }
  },
);

// Create service
export const createService = createAsyncThunk(
  "services/createService",
  async (serviceData: CreateService, { rejectWithValue }) => {
    try {
      const response = await servicesApi.create(serviceData);
      return response.data.data.service;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create service",
      );
    }
  },
);

// Update service
export const updateService = createAsyncThunk(
  "services/updateService",
  async (
    { id, serviceData }: { id: string; serviceData: UpdateService },
    { rejectWithValue },
  ) => {
    try {
      const response = await servicesApi.update(id, serviceData);
      return response.data.data.service;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update service",
      );
    }
  },
);

// Delete service
export const deleteService = createAsyncThunk(
  "services/deleteService",
  async (
    // `force` carries on past the server's "this is still in use" refusal. The
    // service is archived either way, so forcing it is safe — it only means the
    // decision was made by a person rather than silently.
    arg: string | { id: string; force?: boolean },
    { rejectWithValue },
  ) => {
    const id = typeof arg === "string" ? arg : arg.id;
    const force = typeof arg === "string" ? false : !!arg.force;
    try {
      await servicesApi.delete(force ? `${id}?force=true` : id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete service",
      );
    }
  },
);

const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentService: (state) => {
      state.currentService = null;
    },
    resetServiceState: () => {
      return initialState;
    },
    /**
     * Empty both listings, and say a fetch is coming.
     *
     * The two tabs read different slices, so switching between them would
     * otherwise show whatever that tab held when it was last left — rows from
     * an older search, under a header describing the new one — until the fresh
     * request landed. Clearing first turns that flash of stale data into the
     * skeleton a first load already shows.
     *
     * `loading` goes up here rather than waiting for the request's own pending
     * action: effects run after paint, so the frame between the tab switch and
     * the dispatch would otherwise be painted as an empty listing — "no
     * services yet", for a tab that has not been asked yet. The caller
     * dispatches the fetch in the same handler.
     */
    clearListings: (state) => {
      state.services = [];
      state.clientGroups = [];
      state.clientGroupsSearch = "";
      state.pagination = { ...initialState.pagination };
      state.clientGroupsPagination = { ...initialState.clientGroupsPagination };
      state.isLoaded = false;
      state.loading = true;
    },
    setIsLoaded: (state, action) => {
      state.isLoaded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All
      .addCase(getServices.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isLoaded = false;
      })
      .addCase(getServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload.services;
        state.pagination = action.payload.pagination;
        state.error = null;
        state.isLoaded = true;
      })
      .addCase(getServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isLoaded = false;
      })

      // Get client groups
      .addCase(getClientServiceGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClientServiceGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.clientGroups = action.payload.groups;
        // Taken off the request, not off the box: by the time an answer lands
        // the box may hold the next question, and these rows answer this one.
        state.clientGroupsSearch = action.meta.arg?.search?.trim() ?? "";
        state.clientGroupsPagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getClientServiceGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get One
      .addCase(getService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getService.fulfilled, (state, action) => {
        state.loading = false;
        state.currentService = action.payload;
        state.error = null;
      })
      .addCase(getService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.loading = false;
        state.services.unshift(action.payload);
        state.error = null;
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        const index = state.services.findIndex(
          (service) => service._id === action.payload._id,
        );
        if (index !== -1) {
          state.services[index] = action.payload;
        }
        if (state.currentService?._id === action.payload._id) {
          state.currentService = action.payload;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.services = state.services.filter(
          (service) => service._id !== action.payload,
        );
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentService,
  clearListings,
  resetServiceState,
  setIsLoaded,
} = servicesSlice.actions;

export default servicesSlice.reducer;
